import classNames from 'classnames';
import React, {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
} from 'react';

import styles from './IconContainer.module.css';
import { IconFioriAlert, IconFioriError } from '../../icons/v1';

type IconContainerVariant =
  | 'blue'
  | 'indigo'
  | 'teal'
  | 'raspberry'
  | 'hazelnut'
  | 'warning'
  | 'error';
type IconSize = 'small' | 'large';

export interface IconContainerProps {
  /**
   * Optional class name to add to the container element.
   * Do not alter the look of the component! Should be used only for sizing and positioning.
   */
  className?: string;
  /**
   * The icon to be shown.
   * It should preferably be a JSX element of an icon component imported
   * from `@sapappgyver/appgyver-design-system-icons`.
   */
  icon: ReactNode;
  /**
   * Label text for the icon container.
   */
  label: string;
  /**
   * Click event handler for the icon container.
   */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /**
   * Key down event handler for the icon container.
   */
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
  /**
   * Whether to show the label or only show the icon.
   */
  showLabel?: boolean;
  /**
   * The size of the icon container.
   */
  size?: IconSize;
  /**
   * Optional inline styles to add to the container element.
   * Do not alter the look of the component! Should be used only for sizing and positioning.
   */
  style?: CSSProperties;
  /**
   * The variant of the icon container.
   */
  variant?: IconContainerVariant;
}

const containerSizingBySize = new Map<IconSize, string>([['large', styles.large]]);

const getIconComponentByVariant = (
  variant: IconContainerVariant,
  specifiedIcon: ReactNode,
): ReactNode => {
  switch (variant) {
    case 'warning':
      return <IconFioriAlert />;
    case 'error':
      return <IconFioriError />;
    default:
      return specifiedIcon;
  }
};

/**
 * Icon container is equivalent of Horizon Avatar component.
 * @deprecated This component is deprecated. Consider using the Avatar component instead.
 */
export const IconContainer = forwardRef<
  HTMLButtonElement | HTMLDivElement,
  IconContainerProps
>(
  (
    {
      icon,
      label,
      showLabel = true,
      onClick,
      onKeyDown,
      variant = 'blue',
      size = 'small',
      className,
      style,
    },
    ref,
  ) => {
    const interactive = [onClick, onKeyDown].some(
      (handler) => typeof handler === 'function',
    );
    const themeColor = styles[variant];
    const containerSizing = containerSizingBySize.get(size);
    const iconToInject = getIconComponentByVariant(variant, icon);
    const containerChildren = (
      <>
        {iconToInject ? (
          <span className={classNames(styles.icon)}>{iconToInject}</span>
        ) : (
          ''
        )}
        <span
          className={classNames(
            styles.label,
            showLabel ? undefined : styles['visually-hidden'],
          )}
        >
          {label}
        </span>
      </>
    );
    const classes = classNames(
      styles.iconContainer,
      showLabel ? styles['expanded-with-label'] : styles['icon-only'],
      interactive ? undefined : styles['not-interactive'],
      !showLabel ? containerSizing : undefined,
      themeColor,
      className,
    );

    return interactive ? (
      <button
        aria-label={label}
        className={classes}
        onClick={onClick}
        onKeyDown={onKeyDown}
        ref={ref as ForwardedRef<HTMLButtonElement>}
        style={style}
        title={label}
        type="button"
      >
        {containerChildren}
      </button>
    ) : (
      <div
        aria-label={label}
        className={classes}
        ref={ref as ForwardedRef<HTMLDivElement>}
        style={style}
        title={label}
      >
        {containerChildren}
      </div>
    );
  },
);
